import { isNotUndefined } from 'infrastructure/functions';

import { spo2ClinicalAssessmentList } from '../../templates/nurse-yellow';
import {
  getAssessments,
  getDecisionMakingNextSteps,
  maTaskDelegated,
  patientAcknowledgement,
  patientAgreeability,
  reviewedReadings,
} from '../helpers';

import type { TNurseSpo2YellowDataOptional } from '../../types';

export const spo2 = (data: TNurseSpo2YellowDataOptional) => {
  let text = '';

  const hasSpo2Values = data.lowestSpo2 && data.highestSpo2;
  const hasPulseValues = data.lowestPulse && data.highestPulse;
  const hasTrendValues = data.typeofTrend;
  const showReviewText = hasSpo2Values || hasPulseValues || hasTrendValues;
  const reviewedReadingsText = reviewedReadings({
    template: 'RN',
    type: 'SpO2',
    atRiskNumber: showReviewText ? 1 : undefined,
    thresholds: [
      {
        name: 'SpO2',
        lowValue: data.lowestSpo2,
        highValue: data.highestSpo2,
      },
      {
        name: 'pulse',
        lowValue: data.lowestPulse,
        highValue: data.highestPulse,
      },
      {
        name: 'trending',
        value: data.typeofTrend,
      },
    ],
  });
  if (reviewedReadingsText.trim()) text += `${reviewedReadingsText}`;
  if (data.typeofTrendComment?.trim()) {
    text += `${data.typeofTrendComment}. `;
  }

  if (isNotUndefined(data.contactWithPatient)) {
    text += '\n';
    if (data.contactWithPatient) text += `Phone call completed. `;
    else text += `Phone call attempted, unable to reach/left voicemail. `;
  }

  if (
    isNotUndefined(data.noCallMadeRationale) &&
    data.noCallMadeRationale !== 'other'
  ) {
    text += '\n';
    text += `No call made to patient due to `;
    text += `${data.noCallMadeRationale}. `;
  }
  if (data.noCallMadeOtherComment?.trim()) {
    if (data.noCallMadeRationale === undefined) text += '\n';
    text += `${data.noCallMadeOtherComment?.trim()}. `;
  }

  const {
    isAllAssessmentsFalse,
    assessmentTrueTitles,
    assessmentFalseTitles,
    assessmentTrueNames,
    assessmentFalseNames,
  } = getAssessments(spo2ClinicalAssessmentList, data);
  if (isAllAssessmentsFalse) {
    text += '\n';
    text += `Patient denies any signs or symptoms of concern. `;
  } else if (
    assessmentTrueNames.length > 0 ||
    assessmentFalseNames.length > 0
  ) {
    text += '\n';
    if (assessmentTrueNames.length > 0) {
      text += `Patient reports the following signs/symptoms: ${assessmentTrueTitles
        .join(', ')
        .toLowerCase()}. `;
    }
    if (assessmentFalseNames.length > 0) {
      text += `Patient denies the following signs/symptoms: ${assessmentFalseTitles
        .join(', ')
        .toLowerCase()}. `;
    }
  }
  if (data.clinicalAssessmentComment?.trim())
    text += `${data.clinicalAssessmentComment}. `;

  if (isNotUndefined(data.reviewEscalationProtocol)) {
    text += '\n';
    text += `Clinic protocols reviewed, reading ${
      data.reviewEscalationProtocol ? 'requires' : 'does not require '
    } escalation. `;
  }
  if (data.reviewEscalationProtocolComment?.trim()) {
    if (data.reviewEscalationProtocol === undefined) text += '\n';
    text += `${data.reviewEscalationProtocolComment}. `;
  }

  if (isNotUndefined(data.emergencyCareAdvised)) {
    text += '\n';
    text += `Emergency care ${
      data.emergencyCareAdvised ? 'indicated' : 'not indicated'
    } at this time. `;
  }
  if (data.emergencyCareAdvisedComment?.trim()) {
    if (data.emergencyCareAdvised === undefined) text += '\n';
    text += `${data.emergencyCareAdvisedComment?.trim()}. `;
  }

  const hasFactors =
    isNotUndefined(data.potentialContributingFactors) &&
    data.potentialContributingFactors.length > 0;
  if (hasFactors) {
    text += '\n';
    text +=
      'Patient identified the following lifestyle factors as contributing to their at-risk readings: ';
    text += `${data.potentialContributingFactors?.join(', ')}. `;
  }
  if (data.potentialContributingFactorsComment?.trim()) {
    if (!hasFactors) text += '\n';
    text += `${data.potentialContributingFactorsComment}. `;
  }

  const stepsList = getDecisionMakingNextSteps(
    data.decisionMakingNextSteps,
    data,
  );
  if (isNotUndefined(stepsList) && stepsList.length > 0) {
    text += '\n';
    text += 'Nursing team took the following actions on behalf of patient: ';
    text += `${stepsList.join(', ')}. `;
  }

  const educationItems = data.patientEducationProvided?.join(', ');
  const hasEducationItems =
    isNotUndefined(educationItems) && educationItems.length > 0;
  if (hasEducationItems) {
    text += '\n';
    text += `Provided education to patient regarding: ${educationItems}. `;
  }
  if (data.patientEducationProvidedComment?.trim()) {
    if (!hasEducationItems) text += '\n';
    text += `${data.patientEducationProvidedComment}. `;
  }

  if (isNotUndefined(data.digitalEngagementEducationMethod)) {
    text += '\n';
    text += `Sent education to patient via ${data.digitalEngagementEducationMethod}. `;
  }
  if (data.digitalEngagementEducationMethodComment?.trim()) {
    if (data.digitalEngagementEducationMethod === undefined) text += '\n';
    text += `${data.digitalEngagementEducationMethodComment}. `;
  }

  const maTaskDelegatedText = maTaskDelegated(
    data.maTasksDelegated,
    data.maTasksDelegatedList,
    data.maTasksDelegatedListComment,
  );
  if (maTaskDelegatedText.trim()) {
    text += `\n${maTaskDelegatedText}`;
  }
  if (data.maTasksDelegatedComment?.trim()) {
    if (!maTaskDelegatedText) text += '\n';
    text += `${data.maTasksDelegatedComment}. `;
  }

  if (patientAcknowledgement(data.patientAcknowledgement)?.trim()) {
    text += '\n';
    text += patientAcknowledgement(data.patientAcknowledgement);
  }

  if (patientAgreeability(data.patientAgreeability)?.trim()) {
    text += '\n';
    text += patientAgreeability(data.patientAgreeability);
  }
  if (data.patientAgreeabilityComment?.trim()) {
    if (data.patientAgreeability === undefined) text += '\n';
    text += `${data.patientAgreeabilityComment}. `;
  }

  return text;
};
